import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELED', label: 'Canceled' },
];

const defaultTimezone =
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';

const toDateTimeLocal = (date) => {
  if (!date) {
    return '';
  }

  const value = new Date(date);
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 16);
};

const toIsoDate = (dateTimeLocal) => {
  if (!dateTimeLocal) {
    return null;
  }

  return new Date(dateTimeLocal).toISOString();
};

const toFormState = (groupCall = {}) => ({
  title: groupCall.title || '',
  desc: groupCall.desc || '',
  scheduledStartAt: toDateTimeLocal(groupCall.scheduledStartAt),
  scheduledEndAt: toDateTimeLocal(groupCall.scheduledEndAt),
  timezone: groupCall.timezone || defaultTimezone,
  meetingUrl: groupCall.meetingUrl || '',
  recordingUrl: groupCall.recordingUrl || '',
  status: groupCall.status || 'DRAFT',
});

export default function GroupCallForm({
  groupCall,
  onCancel,
  onSubmit,
  saving,
}) {
  const [form, setForm] = useState(toFormState(groupCall));

  useEffect(() => {
    setForm(toFormState(groupCall));
  }, [groupCall]);

  const handleChange = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    onSubmit({
      title: form.title,
      desc: form.desc,
      scheduledStartAt: toIsoDate(form.scheduledStartAt),
      scheduledEndAt: toIsoDate(form.scheduledEndAt),
      timezone: form.timezone,
      meetingUrl: form.meetingUrl || null,
      recordingUrl: form.recordingUrl || null,
      status: form.status,
      access: 'PRO',
    });
  };

  return (
    <Card>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                name="title"
                onChange={handleChange}
                required
                value={form.title}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                name="desc"
                onChange={handleChange}
                rows={4}
                value={form.desc}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                label="Start"
                name="scheduledStartAt"
                onChange={handleChange}
                helperText={`Timezone: ${form.timezone}`}
                type="datetime-local"
                value={form.scheduledStartAt}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                InputLabelProps={{ shrink: true }}
                fullWidth
                label="End"
                name="scheduledEndAt"
                onChange={handleChange}
                helperText={`Timezone: ${form.timezone}`}
                type="datetime-local"
                value={form.scheduledEndAt}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Timezone"
                name="timezone"
                onChange={handleChange}
                value={form.timezone}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="group-call-status-label">Status</InputLabel>
                <Select
                  label="Status"
                  labelId="group-call-status-label"
                  name="status"
                  onChange={handleChange}
                  value={form.status}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Meeting URL"
                name="meetingUrl"
                onChange={handleChange}
                value={form.meetingUrl}
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Recording URL"
                name="recordingUrl"
                onChange={handleChange}
                value={form.recordingUrl}
                variant="outlined"
              />
            </Grid>
          </Grid>
          <Box display="flex" gap={2} mt={3}>
            <Button
              color="secondary"
              disabled={saving}
              type="submit"
              variant="contained"
            >
              Save
            </Button>
            <Button disabled={saving} onClick={onCancel} variant="outlined">
              Cancel
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
}
